import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { useInnerHeroImage } from '../hooks/useInnerHeroImage';
import {
  INNER_HERO_SUBTITLE_CLASS,
  INNER_HERO_TITLE_CLASS,
  InnerHeroBackground,
  InnerHeroShell,
} from '../ui/InnerHeroShell';

/**
 * Gallery page hero. Keeps the `gallery-hero.*` keys the shared PageHero
 * instance saved to, so existing edits carry over.
 */

const TITLE = '<p>Gallery</p>';
const SUBTITLE = '<p>Interior, Exterior &amp; Community Photos</p>';

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Gallery banner' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'A visually striking photo of the property or community that works as a showcase for the photo gallery. Alt text describes the chosen photo.',
    context:
      'Full-bleed inner page hero banner behind the "Gallery" title, under a dark overlay; crops to a wide landscape strip. Must be sharp, well lit and uncluttered — never a floor plan drawing, map, logo, or graphic. Use a photo not already used by the homepage hero or the other inner page heroes (Floor Plans, Amenities, Neighborhood, Virtual Tour, Contact). Reuse a photo only if no unused property photo fits.',
  },
};

export function GalleryHero() {
  const bg = useInnerHeroImage('gallery-hero.bg_image', bgImageDef, 'gallery', {
    altText: 'Gallery banner',
  });

  return (
    <InnerHeroShell
      background={
        <EditableSlotGroup
          groupPath="gallery-hero.bg_image"
          definition={bgImageDef}
          className="absolute inset-0"
        >
          <InnerHeroBackground image={bg.image} alt={bg.alt} />
        </EditableSlotGroup>
      }
    >
      <RichTextContent
        path="gallery-hero.subtitle"
        label="Subtitle"
        defaultValue={SUBTITLE}
        as="h2"
        className={INNER_HERO_SUBTITLE_CLASS}
      />
      <RichTextContent
        path="gallery-hero.title"
        label="Title"
        defaultValue={TITLE}
        as="h1"
        className={INNER_HERO_TITLE_CLASS}
      />
    </InnerHeroShell>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'GalleryHero',
  displayName: 'Gallery Hero',
  tokens: {
    'gallery-hero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Interior, Exterior &amp; Community Photos</p>',
    },
    'gallery-hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Gallery</p>',
    },
  },
  slotGroups: {
    'gallery-hero.bg_image': bgImageDef,
  },
};

(GalleryHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default GalleryHero;
